import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Header } from "@/pages/admin/AdminDashboard";
import { Card, Field } from "@/pages/admin/AdminProfile";

const empty = { name: "", category: "Frontend", level: 80, icon: "Code2", order: 0 };

export default function AdminSkills() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const res = await api.get("/skills");
        setItems(res.data);
        setLoading(false);
    };
    useEffect(() => {
        load();
    }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this skill?")) return;
        await api.delete(`/admin/skills/${id}`);
        toast.success("Skill deleted");
        load();
    };

    return (
        <div data-testid="admin-skills-page">
            <Header
                title="Skills"
                subtitle="Atur kategori dan tingkat penguasaan tools yang Anda gunakan."
                action={
                    <button
                        onClick={() => setEditing({ ...empty })}
                        data-testid="skill-new"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand text-white hover:bg-brand-hover"
                    >
                        <Plus size={16} /> New skill
                    </button>
                }
            />

            {loading ? (
                <div className="mono-label">Loading…</div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-white/5">
                    <table className="w-full text-sm">
                        <thead className="bg-ink-800/60 text-ink-200">
                            <tr>
                                <th className="text-left p-4 font-mono text-xs uppercase tracking-wider">Skill</th>
                                <th className="text-left p-4 font-mono text-xs uppercase tracking-wider">Category</th>
                                <th className="text-left p-4 font-mono text-xs uppercase tracking-wider">Level</th>
                                <th className="text-left p-4 font-mono text-xs uppercase tracking-wider">Icon</th>
                                <th className="text-right p-4 font-mono text-xs uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {items.map((s) => (
                                <tr key={s.id} className="hover:bg-white/5">
                                    <td className="p-4 text-white font-medium">{s.name}</td>
                                    <td className="p-4 text-ink-100">{s.category}</td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-28 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                                <div className="h-full bg-brand" style={{ width: `${s.level}%` }} />
                                            </div>
                                            <span className="font-mono text-xs text-ink-200">{s.level}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 font-mono text-xs text-ink-200">{s.icon}</td>
                                    <td className="p-4 text-right">
                                        <div className="inline-flex gap-1">
                                            <button onClick={() => setEditing({ ...s })} data-testid={`skill-edit-${s.id}`} className="p-2 rounded hover:bg-white/10 text-ink-200 hover:text-white">
                                                <Pencil size={14} />
                                            </button>
                                            <button onClick={() => remove(s.id)} data-testid={`skill-delete-${s.id}`} className="p-2 rounded hover:bg-red-500/10 text-ink-200 hover:text-red-400">
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr><td colSpan="5" className="p-12 text-center text-ink-300">No skills yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && <SkillEditor initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
        </div>
    );
}

function SkillEditor({ initial, onClose, onSaved }) {
    const [data, setData] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial.id);
    const update = (k) => (e) => {
        const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setData((s) => ({ ...s, [k]: v }));
    };

    const save = async () => {
        setSaving(true);
        try {
            if (isEdit) await api.put(`/admin/skills/${initial.id}`, data);
            else await api.post("/admin/skills", data);
            toast.success(isEdit ? "Skill updated" : "Skill created");
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/70 backdrop-blur" data-testid="skill-editor">
            <div className="w-full max-w-md bg-ink-900 border-l border-white/10 overflow-y-auto">
                <div className="sticky top-0 z-10 bg-ink-900/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="mono-label">{isEdit ? "Edit skill" : "New skill"}</div>
                        <h2 className="font-display text-xl text-white">{data.name || "Untitled"}</h2>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={save} disabled={saving} data-testid="skill-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand text-white hover:bg-brand-hover">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                        </button>
                        <button onClick={onClose} className="p-2 rounded-md hover:bg-white/10 text-ink-200"><X size={16} /></button>
                    </div>
                </div>
                <div className="p-6 space-y-5">
                    <Card title="Skill details">
                        <Field label="Name"><input className="ainp" value={data.name} onChange={update("name")} /></Field>
                        <Field label="Category">
                            <input
                                list="skill-categories"
                                className="ainp"
                                value={data.category}
                                onChange={update("category")}
                                placeholder="Pilih atau ketik kategori baru..."
                            />
                            <datalist id="skill-categories">
                                {["Frontend", "Backend", "DevOps", "Design", "Mobile", "Data", "AI/ML", "Other"].map((c) => (
                                    <option key={c} value={c} />
                                ))}
                            </datalist>
                        </Field>
                        <Field label={`Level (${data.level})`}>
                            <input type="range" min="0" max="100" value={data.level} onChange={update("level")} className="w-full accent-brand" />
                        </Field>
                        <Field label="Icon (lucide-react name)">
                            <input className="ainp" value={data.icon} onChange={update("icon")} placeholder="Code2" />
                        </Field>
                        <Field label="Order"><input type="number" className="ainp" value={data.order} onChange={update("order")} /></Field>
                    </Card>
                </div>
            </div>
        </div>
    );
}
